import {
  CounterConfig,
  CounterQuestionConfig,
  CounterType,
  DateTypeFilter,
  getFullName,
  Name,
  RoutineType,
  SortDirectionEnum,
  useCustomQuery,
  useFindByQuery,
  WorkflowRoutineConfig,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import Toast from 'react-native-toast-message';
import { useDataPlateColumns } from '../../accounts/components/dataExport/useDataPlateColumns';
import {
  barcodeColumnKey,
  buildSubmissionRows,
  detectBarcodeColumns,
  detectStartEndTimeColumns,
  endTimeColumnKey,
  ExportRow,
  sanitizeCsvString,
  SubmissionResponse,
  supersededSubmissionIds,
} from './routineExportFormat';

// Gap between the per-file link clicks. Firing them all in one tick gets the
// tail of the batch dropped by the browser's download handling.
const DOWNLOAD_INTERVAL_MS = 100;

export type ExportDateFilter = {
  dateType: DateTypeFilter;
  startDate?: string;
  endDate?: string;
};

type Props = {
  routineIds: string[];
  workspaceId: string;
  /**
   * Monotonic counter — one increment per user request to download. The export
   * fires once per increment, as soon as the data it needs has arrived, and
   * then goes idle. No outstanding request, no download.
   */
  requestId: number;
  dateFilter?: ExportDateFilter;
  /**
   * When true, each individual answer value (barcode scan, image, multi-/single-
   * select option) is exported as its own row instead of being consolidated into
   * one cell. Only affects Workflow routines; single-value routine types
   * (Counter/Status/QuickConfirm) are unchanged.
   */
  splitToRows?: boolean;
  /**
   * When true, appends the asset's description & details plus the latest
   * submission of every one-time ("data plate") routine on the asset.
   */
  addAllAssetInfo?: boolean;
  onExportingChange?: (isExporting: boolean) => void;
};

type CsvHeader = { label: string; key: string };

type CsvFile = {
  headers: CsvHeader[];
  rows: ExportRow[];
  filename: string;
};

// react-csv hangs the anchor it renders off the CSVLink instance as `link`;
// the shipped types don't declare it.
type CsvLinkHandle = { link?: HTMLAnchorElement | null };

type SubmissionData = {
  responses?: SubmissionResponse[];
  auditLog?: { auditLog?: unknown[] };
  status?: string;
  openEndedResponse?: string;
  count?: number;
  userCount?: number;
  confirmedValue?: string;
};

type SubmissionItem = {
  id: string;
  routine: { id: string };
  routineVersion?: { id: string } | null;
  asset: { id: string; name: string; description?: string; details?: string };
  workspaceAsset: { groups?: { name?: string }[] | null };
  createdAt: string;
  adjustCreationDate?: string | null;
  createdBy: { name?: Name };
  revisionOf?: { id?: string } | null;
  data: SubmissionData;
};

type RoutineVersion = { id: string; config?: WorkflowRoutineConfig };

type DateRange = { ge?: string; le?: string };

const buildDateRange = (dateFilter?: ExportDateFilter): DateRange => {
  if (!dateFilter || dateFilter.dateType === DateTypeFilter.ALL) {
    return {};
  }

  if (dateFilter.dateType === DateTypeFilter.CUSTOMDATE) {
    const range: DateRange = {};
    if (dateFilter.startDate) {
      // Send actual UTC time
      range.ge = new Date(dateFilter.startDate).toISOString();
    }
    if (dateFilter.endDate) {
      // Send actual UTC time at end of day
      const endDate = new Date(dateFilter.endDate);
      endDate.setHours(23, 59, 59, 999);
      range.le = endDate.toISOString();
    }
    return range;
  }

  const now = new Date();
  const startDate = new Date(now);

  switch (dateFilter.dateType) {
    case DateTypeFilter.TODAY:
      break;
    case DateTypeFilter.THISWEEK:
      startDate.setDate(now.getDate() - 6);
      break;
    case DateTypeFilter.THISMONTH:
      startDate.setDate(now.getDate() - 29);
      break;
    default:
      return {};
  }
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(now);
  endDate.setHours(23, 59, 59, 999);

  return { ge: startDate.toISOString(), le: endDate.toISOString() };
};

const effectiveSubmissionDate = (item: SubmissionItem) =>
  new Date(item.adjustCreationDate ?? item.createdAt);

/**
 * Builds one CSV per selected routine and hands them to the browser.
 *
 * Renders nothing visible: it holds a hidden react-csv link per file and clicks
 * them when a download request fires. A request is one-shot — `requestId`
 * arms it, it fires once the queries it feeds on have settled, and the
 * component goes idle again. Everything the CSVs are built from is derived
 * from the live query results rather than copied into state, so a request can
 * never hand the browser the previous selection's files.
 */
export const DownloadRoutine = (props: Props) => {
  const {
    routineIds,
    workspaceId,
    requestId,
    dateFilter,
    splitToRows = false,
    addAllAssetInfo = false,
    onExportingChange,
  } = props;
  const { t } = useTranslation();

  const csvRefs = useRef<(CsvLinkHandle | null)[]>([]);
  const clickTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [pendingRequestId, setPendingRequestId] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (requestId > 0) {
      setPendingRequestId(requestId);
    }
  }, [requestId]);

  // Drives the export queries: they run while a request is outstanding or its
  // files are still going out, and are idle the rest of the time.
  const isExporting = pendingRequestId > 0 || isDownloading;

  useEffect(() => {
    onExportingChange?.(isExporting);
  }, [isExporting, onExportingChange]);

  const { data: routinesData } = useFindByQuery({
    query: GET_EXPORT_ROUTINES,
    modelName: 'WorkspaceRoutine',
    variables: {
      filter: { id: { in: routineIds } },
      orderBy: { field: 'order', order: SortDirectionEnum.Asc },
    },
    options: { enabled: isExporting },
  });

  const routines = useMemo<WorkspaceRoutine[]>(
    () => routinesData?.items ?? [],
    [routinesData]
  );

  // Recomputed per render on purpose: preset windows resolve against "now", so
  // memoizing pins yesterday's window after midnight. The resolved ISO strings
  // are stable within a day, so nothing downstream churns.
  const dateRange = buildDateRange(dateFilter);
  const { ge: rangeStart, le: rangeEnd } = dateRange;
  const hasDateFilter = !!rangeStart || !!rangeEnd;

  const { data: submissionsData, isFetching: isSubmissionsFetching } =
    useCustomQuery({
      query: GET_ROUTINE_DATA,
      cacheKey: ['exportRoutineData', routineIds, dateFilter],
      variables: {
        filter: {
          routine: { in: routineIds },
          ...(hasDateFilter && {
            or: [{ adjustCreationDate: dateRange }, { createdAt: dateRange }],
          }),
          isDraft: { ne: true },
        },
        orderBy: { field: 'createdAt', order: SortDirectionEnum.Asc },
      },
      options: {
        enabled: isExporting,
        gcTime: 10000,
        staleTime: 10000,
      },
    });

  const submissions = useMemo<SubmissionItem[]>(
    () => submissionsData?.findAssetRoutines?.items ?? [],
    [submissionsData]
  );

  const versionIds = useMemo(() => {
    const ids = new Set<string>();
    submissions.forEach((item) => {
      if (item.routineVersion?.id) {
        ids.add(item.routineVersion.id);
      }
    });
    return Array.from(ids);
  }, [submissions]);

  const { data: versionData, isLoading: isVersionsLoading } = useFindByQuery({
    query: GET_ROUTINE_VERSIONS,
    modelName: 'workspaceRoutineVersion',
    variables: {
      filter: { id: { in: versionIds } },
    },
    options: {
      enabled: versionIds.length > 0,
    },
  });

  const versionConfigsMap = useMemo(() => {
    const map = new Map<string, RoutineVersion>();
    versionData?.items?.forEach((version: RoutineVersion) =>
      map.set(version.id, version)
    );
    return map;
  }, [versionData]);

  const dataPlate = useDataPlateColumns(
    workspaceId,
    addAllAssetInfo && isExporting
  );

  // Everything a CSV is built from has to have landed before a pending request
  // fires, or the file goes out with rows or columns still missing. Each clause
  // waits for its query to settle rather than to return something: these hooks
  // carry no error state, so a stricter wait strands the request forever on a
  // failed fetch.
  const isExportDataReady =
    !!routinesData &&
    !!submissionsData &&
    !isSubmissionsFetching &&
    (versionIds.length === 0 || (!!versionData && !isVersionsLoading)) &&
    !(addAllAssetInfo && dataPlate.isLoading);

  const baseHeaders = useMemo<CsvHeader[]>(
    () => [
      { label: 'Asset', key: 'asset' },
      { label: 'Created By', key: 'createdBy' },
      { label: 'Adjusted Date', key: 'adjustedDate' },
      { label: 'Created Date', key: 'createdDate' },
      { label: 'Created Time', key: 'createdTime' },
      { label: 'Creation UTC', key: 'creationUtc' },
      { label: 'Groups', key: 'groups' },
      { label: 'Is Edit', key: 'isEdit' },
    ],
    []
  );

  // Extra asset columns inserted after the base columns when "Add all asset
  // info" is on. The data-plate columns are appended at the far right and are
  // already empty when the option is off (the hook returns no headers).
  const assetInfoHeaders = useMemo<CsvHeader[]>(
    () =>
      addAllAssetInfo
        ? [
            { label: 'Description', key: 'description' },
            { label: 'Details', key: 'details' },
          ]
        : [],
    [addAllAssetInfo]
  );

  const createCounterColumns = useCallback(
    (baseLabel: string, questionId: string): CsvHeader[] => {
      return [
        { label: `${baseLabel} - Starting Value`, key: `${questionId}_start` },
        { label: `${baseLabel} - Delta`, key: `${questionId}_delta` },
        { label: `${baseLabel} - Ending Value`, key: questionId },
      ];
    },
    []
  );

  const formatCounterData = useCallback((response: SubmissionResponse) => {
    const count = Number(response.count ?? 0);
    const userCount = Number(response.userCount ?? 0);
    const startingValue = count - userCount;

    return {
      [`${response.questionId}_start`]: startingValue.toString(),
      [`${response.questionId}_delta`]: userCount.toString(),
      [response.questionId]: count.toString(),
    };
  }, []);

  const getBaseRowData = useCallback((item: SubmissionItem): ExportRow => {
    const createdDate = item.createdAt ? new Date(item.createdAt) : null;
    const adjustedDate = item.adjustCreationDate
      ? new Date(item.adjustCreationDate)
      : null;

    return {
      asset: sanitizeCsvString(item.asset.name),
      description: sanitizeCsvString(item.asset.description ?? ''),
      details: sanitizeCsvString(item.asset.details ?? ''),
      createdBy: sanitizeCsvString(getFullName(item.createdBy.name)),
      adjustedDate: adjustedDate ? adjustedDate.toLocaleDateString() : '',
      createdDate: createdDate ? createdDate.toLocaleDateString() : '',
      createdTime: createdDate ? createdDate.toLocaleTimeString() : '',
      creationUtc: createdDate ? createdDate.toISOString() : '',
      groups:
        item.workspaceAsset.groups
          ?.filter((group) => group?.name)
          .map((group) => group.name)
          .join(', ') ?? '',
      isEdit: item.data?.auditLog?.auditLog?.length ? 'Yes' : '',
    };
  }, []);

  const createWorkflowData = useCallback(
    (items: SubmissionItem[], workspaceRoutine: WorkspaceRoutine): CsvFile => {
      // Collect all unique questions from all versions in the data
      const allQuestionsMap = new Map<
        string,
        { id: string; instructions: string; order: number }
      >();
      const multiColumnCounterIds = new Set<string>();

      items.forEach((item) => {
        const versionId = item.routineVersion?.id;
        const version = versionId ? versionConfigsMap.get(versionId) : null;

        version?.config?.pages?.forEach((page) => {
          page.questions?.forEach((question) => {
            allQuestionsMap.set(question.id, {
              id: question.id,
              instructions: question.instructions || '',
              order: question.order,
            });

            if (question.type === 'COUNTER') {
              const counterType = (question as CounterQuestionConfig)
                .counterType;
              if (
                counterType === CounterType.Cumulative ||
                counterType === CounterType.Tally
              ) {
                multiColumnCounterIds.add(question.id);
              }
            }
          });
        });

        // Check responses for nested counter routines
        item.data.responses?.forEach((response) => {
          if (
            response.counterType === CounterType.Cumulative ||
            response.counterType === CounterType.Tally
          ) {
            multiColumnCounterIds.add(response.questionId);
          }
        });
      });

      // Sort questions by order to maintain some consistency
      const allQuestions = Array.from(allQuestionsMap.values()).sort(
        (a, b) => a.order - b.order
      );

      // Counter questions expand into multiple columns via createCounterColumns.
      // Barcode questions likewise become multi-column — Barcode / Qty / Memo —
      // in BOTH export modes (like counters and start/end times). Split-to-rows
      // only decides whether multiple scans spread DOWN into one row each or
      // comma-join on the submission's single row (see buildSubmissionRows).
      // Every other question gets a single column. Barcode Qty/Memo columns are
      // only emitted when the data actually carries them.
      const barcodeColumns = detectBarcodeColumns(
        items.map((item) => ({ responses: item.data.responses }))
      );

      // "Start & end times" questions split into a Start and an End column
      // (unlike counters/barcodes, this applies regardless of split-to-rows).
      const startEndTimeColumns = detectStartEndTimeColumns(
        items.map((item) => ({ responses: item.data.responses }))
      );

      const headers = [...baseHeaders, ...assetInfoHeaders];
      allQuestions.forEach((question) => {
        const barcodePresence = barcodeColumns.get(question.id);

        if (multiColumnCounterIds.has(question.id)) {
          headers.push(
            ...createCounterColumns(question.instructions, question.id)
          );
        } else if (startEndTimeColumns.has(question.id)) {
          headers.push(
            { label: `${question.instructions} - Start`, key: question.id },
            {
              label: `${question.instructions} - End`,
              key: endTimeColumnKey(question.id),
            }
          );
        } else if (barcodePresence) {
          const hasExtraColumns =
            barcodePresence.quantity || barcodePresence.memo;
          headers.push({
            label: hasExtraColumns
              ? `${question.instructions} - Barcode`
              : question.instructions,
            key: question.id,
          });
          if (barcodePresence.quantity) {
            headers.push({
              label: `${question.instructions} - Qty`,
              key: barcodeColumnKey(question.id, 'qty'),
            });
          }
          if (barcodePresence.memo) {
            headers.push({
              label: `${question.instructions} - Memo`,
              key: barcodeColumnKey(question.id, 'memo'),
            });
          }
        } else {
          headers.push({ label: question.instructions, key: question.id });
        }
      });
      headers.push(...dataPlate.dataPlateHeaders);

      const rows = items.flatMap((item) =>
        buildSubmissionRows(
          item.data.responses,
          {
            ...getBaseRowData(item),
            ...(dataPlate.valuesByAssetId[item.asset.id] ?? {}),
          },
          { splitToRows, multiColumnCounterIds, formatCounterData }
        )
      );

      return {
        headers,
        rows,
        filename: `${workspaceRoutine.config.title}-routine-data.csv`,
      };
    },
    [
      baseHeaders,
      assetInfoHeaders,
      dataPlate,
      getBaseRowData,
      formatCounterData,
      createCounterColumns,
      versionConfigsMap,
      splitToRows,
    ]
  );

  const createNonWorkflowData = useCallback(
    (items: SubmissionItem[], workspaceRoutine: WorkspaceRoutine): CsvFile => {
      const counterType = (workspaceRoutine.config as Partial<CounterConfig>)
        .counterType;
      const isMultiColumn =
        workspaceRoutine.type === RoutineType.Counter &&
        (counterType === CounterType.Cumulative ||
          counterType === CounterType.Tally);

      const headers = [...baseHeaders, ...assetInfoHeaders];

      if (isMultiColumn) {
        headers.push(
          {
            label: `${workspaceRoutine.config.title} - Starting Value`,
            key: 'startingValue',
          },
          { label: `${workspaceRoutine.config.title} - Delta`, key: 'delta' },
          {
            label: `${workspaceRoutine.config.title} - Ending Value`,
            key: 'data',
          }
        );
      } else {
        headers.push({ label: workspaceRoutine.config.title, key: 'data' });
      }
      headers.push(...dataPlate.dataPlateHeaders);

      const rows = items.map((item) => {
        const baseRowData = getBaseRowData(item);
        let data = '';

        switch (workspaceRoutine.type) {
          case RoutineType.Status:
            // Status routine: use status value, or openEndedResponse if user typed custom value
            if (
              item.data.status === 'openEndedResponse' &&
              item.data.openEndedResponse
            ) {
              data = item.data.openEndedResponse;
            } else {
              data = item.data.status || '';
            }
            break;
          case RoutineType.Counter:
            // Counter routine: use count as the value (for Simple it's the only value, for Cumulative/Tally it's the ending value)
            data = item.data.count?.toString() ?? '';
            break;
          case RoutineType.QuickConfirm:
            // QuickConfirm routine: use confirmedValue
            data = item.data.confirmedValue || '';
            break;
          default: {
            // Fallback for any other types - grab first value from data object
            const values = Object.values(item.data).filter(
              (value) => value !== undefined && value !== null
            );
            data = values.length > 0 ? String(values[0]) : '';
          }
        }

        const row: ExportRow = {
          ...baseRowData,
          ...(dataPlate.valuesByAssetId[item.asset.id] ?? {}),
          data,
        };

        if (isMultiColumn) {
          const count = item.data.count ?? 0;
          const userCount = item.data.userCount ?? 0;

          row.startingValue = (count - userCount).toString();
          row.delta = userCount.toString();
        }

        return row;
      });

      return {
        headers,
        rows,
        filename: `${workspaceRoutine.config.title}-routine-data.csv`,
      };
    },
    [baseHeaders, assetInfoHeaders, dataPlate, getBaseRowData]
  );

  const csvFiles = useMemo<CsvFile[]>(() => {
    if (!isExportDataReady) {
      return [];
    }

    const supersededIds = supersededSubmissionIds(submissions);
    const startDate = rangeStart ? new Date(rangeStart) : undefined;
    const endDate = rangeEnd ? new Date(rangeEnd) : undefined;
    const files: CsvFile[] = [];

    routines.forEach((routine) => {
      const routineItems = submissions
        .filter((item) => {
          if (item.routine.id !== routine.id || supersededIds.has(item.id)) {
            return false;
          }
          const effectiveDate = effectiveSubmissionDate(item);
          if (startDate && effectiveDate < startDate) return false;
          if (endDate && effectiveDate > endDate) return false;
          return true;
        })
        .sort(
          (a, b) =>
            effectiveSubmissionDate(a).getTime() -
            effectiveSubmissionDate(b).getTime()
        );

      if (routineItems.length === 0) {
        return;
      }

      files.push(
        routine.type === RoutineType.Workflow
          ? createWorkflowData(routineItems, routine)
          : createNonWorkflowData(routineItems, routine)
      );
    });

    return files;
  }, [
    isExportDataReady,
    submissions,
    routines,
    rangeStart,
    rangeEnd,
    createWorkflowData,
    createNonWorkflowData,
  ]);

  useEffect(() => {
    if (pendingRequestId === 0 || !isExportDataReady) {
      return;
    }

    setPendingRequestId(0);

    if (csvFiles.length === 0) {
      Toast.show({
        type: 'blackToast',
        text1: t('routines.download.noDataTitle'),
        text2: t('routines.download.noDataMessage'),
        position: 'bottom',
        autoHide: true,
        visibilityTime: 2500,
      });
      return;
    }

    setIsDownloading(true);

    // The link handles are captured up front so a selection change landing
    // mid-batch can't retarget the files still to go out. A request that lands
    // mid-batch supersedes it: this one schedules its own set whole, so the
    // superseded clicks are cancelled rather than interleaved with it.
    const links = csvFiles.map((_, index) => csvRefs.current[index]);
    clickTimers.current.forEach(clearTimeout);
    clickTimers.current = links.map((link, index) =>
      setTimeout(() => {
        link?.link?.click();
        if (index === links.length - 1) {
          setIsDownloading(false);
        }
      }, index * DOWNLOAD_INTERVAL_MS)
    );
  }, [pendingRequestId, isExportDataReady, csvFiles, t]);

  useEffect(
    () => () => {
      clickTimers.current.forEach(clearTimeout);
      clickTimers.current = [];
    },
    []
  );

  return (
    <>
      {csvFiles.map((csvFile, index) => (
        <CSVLink
          key={`${index}-${csvFile.filename}`}
          style={{ display: 'none' }}
          ref={(instance: unknown) => {
            csvRefs.current[index] = instance as CsvLinkHandle | null;
          }}
          data={csvFile.rows}
          headers={csvFile.headers}
          filename={csvFile.filename}
        />
      ))}
    </>
  );
};

const GET_EXPORT_ROUTINES = `
  query GetWorkspaceRoutines($filter: WorkspaceRoutineFilter, $orderBy: OrderByInput) {
    findWorkspaceRoutines(filter: $filter, orderBy: $orderBy) {
      items {
        id
        name
        type
        order
        config
      }
    }
  }
`;

const GET_ROUTINE_DATA = `
  query FindAssetRoutines($filter: AssetRoutineFilter, $orderBy: OrderByInput) {
    findAssetRoutines(filter: $filter, orderBy: $orderBy) {
      items {
        routine {
          id
        }
        routineVersion {
          id
        }
        asset {
          id
          name
          description
          details
        }
        workspaceAsset {
          groups {
            name
          }
        }
        createdAt
        adjustCreationDate
        createdBy {
          name {
            first
            last
          }
        }
        id
        revisionOf {
          id
        }
        data
      }
    }
  }
`;

const GET_ROUTINE_VERSIONS = `
  query FindWorkspaceRoutineVersions($filter: WorkspaceRoutineVersionFilter) {
    findWorkspaceRoutineVersions(filter: $filter) {
      items {
        id
        type
        config
      }
    }
  }
`;
